import { BreakPoints, IDetectPhone } from 'shared-types/index';

const NS = 'LocationService';

export default class LocationService {
  static isSSH(url: string = window.location.href): boolean {
    return url.indexOf('https') === 0;
  }

  static isTouchDevice(): boolean {
    return (('ontouchstart' in window) ||
      (navigator.maxTouchPoints > 0) ||
      //@ts-ignore
      (navigator.msMaxTouchPoints > 0));
  }

  // https://stackoverflow.com/questions/9038625/detect-if-device-is-ios/9039885#9039885
  static isIOS(): boolean {
    return (
      (/iPad|iPhone|iPod/.test(navigator.platform) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
      !(window as any).MSStream
    );
  }

  static isAndroid(): boolean {
    return navigator.userAgent.toLowerCase().indexOf('android') > -1;
  }

  /**
   * Detects if user's brower is Safari. It uses the vendor metadata filtering out when the isChromeIPAD
   * returns true. Chrome Browsers on IPAD also have vendor metadata set as "Apple"
   */
  static isSafari(): boolean {
    return navigator.vendor.search('Apple') >= 0 && !LocationService.isChromeIPAD();
  }

  static isWindows(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const windowsPlatforms = /(win32|win64|windows|wince)/i;
    return windowsPlatforms.test(userAgent);
  }

  // https://chromium.googlesource.com/chromium/src.git/+/HEAD/docs/ios/user_agent.md
  /**
   * Detects if the user's browser is Chrome in a Ipad device. The only way to detect it according to the link above
   * is verify the presence of the string "CriOS" on the userAgent property
   */
  static isChromeIPAD(): boolean {
    return navigator.userAgent.search('CriOS') >= 0;
  }

  /**
   * Detects if the outer iframe container (native HTML page) is using a phone, by
   * checking the window's outerWidth. Also checks for iPhone if you need iPhone specifically,
   * which works on XCode iPhone simulator as well.
   */
  static detectPhone(): IDetectPhone {
    return {
      isPhone: window.outerWidth <= BreakPoints.STACKED_BP,
      isIPhone: /iPhone/.test(navigator.platform),
    };
  }

  /**
   * RFC4122 compliant GUID generator for JS that is compact and "good enough" for what we want.
   * Should only get called once per session/app load.
   * Used for better SEQ logging.
   */
  static uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Accepts a url and removes either token param and/or edit query string
   */
  static stripCurrentUrl(url: string, removeToken: boolean, removeEdit: boolean): string {
    if (removeToken) {
      const splitUrl: string[] = url.split('&token=');
      url = splitUrl[0];

      if (splitUrl[1] && splitUrl[1].indexOf('&') !== -1) {
        const trailingParams = splitUrl[1].split('&')[1];
        url += '&' + trailingParams;
      }
    }

    if (removeEdit) {
      url = url.replace('edit?', '?');
    }

    return url;
  }
  
  // Ref: NBI-8786 (doesn't include CONNECT, which just uses localhost)
  static getHostNameBySource(source: 'diary' | 'admin' | 'diary-mobile' |'widget2' | 'abc-widget' | 'gift-cards'): string {
    switch (source) {
      case 'diary':
        return 'restaurant.local';
      case 'admin':
        return 'admin.local';
      case 'diary-mobile':  
        return 'mobile.local';
      case 'widget2':
        return 'widget-v2.local';
      case 'abc-widget':
        return 'group-widget.local';
      case 'gift-cards':
        return 'gift-cards.local';
      default:
        return 'localhost';
    }
  }

  // optionally pass in source from appRoot/app.constants
  static isLocalHost(source?: 'diary' | 'admin' | 'diary-mobile' | 'widget2' | 'abc-widget' | 'gift-cards'): boolean {
    const hostname = window.location.hostname;
    const isRegularLocalhost = hostname === 'localhost' || hostname === '0.0.0.0' || hostname === '127.0.0.1';

    if (isRegularLocalhost) {
      return true;
    }

    if (source) {
      return hostname === this.getHostNameBySource(source);
    }

    return false;
  }

  static deviceDetails(): string {
    const { isPhone, isIPhone } = this.detectPhone();
    let details = `Is Phone: ${isPhone ? 'true' : 'false'}`;

    if (isIPhone || this.isIOS()) {
      details += 'OS: iOS,';
    } else if (this.isAndroid()) {
      details += 'OS: Android,';
    }

    if (this.isSafari()) {
      details += 'Browser: Safari';
    } else if (this.isChromeIPAD()) {
      details += 'Browser: Chrome iPad';
    }

    return details;
  }
  static getMockError(str: string): any {
    if (window.location.search.indexOf(`trigger-error=${str}`) !== -1) {
      throw new Error('Mock Error');
    }
  }

  static openInNewTab = (url: string): void => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
  }
}
